import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatIDR, fileUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useAuth as useAuthLocal } from "../context/AuthContext";
import { useBrand, BRAND_META } from "../context/BrandContext";
import { canEdit as canEditRole } from "../lib/roles";
import ProductCard from "../components/ProductCard";
import StockPill from "../components/StockPill";
import {
    MagnifyingGlass,
    SlidersHorizontal,
    SquaresFour,
    Rows,
    Plus,
} from "@phosphor-icons/react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "../components/ui/sheet";
import { Slider } from "../components/ui/slider";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

// Catalog chips are now sourced dynamically from the active brand's categories
// (Settings → Category Management). No brand-specific category is hardcoded here.

const SORT_OPTIONS = [
    { key: "newest", label: "Newest" },
    { key: "name_asc", label: "Name A–Z" },
    { key: "name_desc", label: "Name Z–A" },
    { key: "price_asc", label: "Price Low → High" },
    { key: "price_desc", label: "Price High → Low" },
    { key: "stock_desc", label: "Stock High → Low" },
    { key: "stock_asc", label: "Stock Low → High" },
];

function productStockTotal(p) {
    if (p.variants?.length) return p.variants.reduce((s, v) => s + (Number(v.stock) || 0), 0);
    return Number(p.stock) || 0;
}

export default function Catalog() {
    const { settings } = useSettings();
    const { user: me } = useAuthLocal();
    const { current: brandId } = useBrand();
    const isHeelsAndTea = brandId === "heelsandtea";

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState(null);
    const [level1, setLevel1] = useState("all");
    const [level2, setLevel2] = useState("all");
    const [sort, setSort] = useState("newest");
    const [view, setView] = useState("catalog");

    const [advCategory, setAdvCategory] = useState("");
    const [carat, setCarat] = useState("");
    const [size, setSize] = useState("");
    const PRICE_MIN = 0;
    const PRICE_MAX = 50_000_000;
    const [price, setPrice] = useState([PRICE_MIN, PRICE_MAX]);
    const priceFilterActive = price[0] > PRICE_MIN || price[1] < PRICE_MAX;

    // Allow deep-links like /catalog?category=Fashion or ?region=japan to apply
    // the matching filter once on mount — used by Settings "View Products" from
    // the delete-blocked modal.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const cat = searchParams.get("category");
        const region = searchParams.get("region");
        const collection = searchParams.get("collection");
        if (cat) setCategory(cat);
        if (region) { setLevel1(region); setLevel2("all"); }
        if (collection) { setLevel1(collection); setLevel2("all"); }
        if (cat && isHeelsAndTea) setLevel2(cat);
        if (cat || region || collection) {
            // Consume the params so later brand switches don't re-apply them.
            const next = new URLSearchParams(searchParams);
            next.delete("category"); next.delete("region"); next.delete("collection");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setCategory(null);
        setAdvCategory("");
        setLevel1("all");
        setLevel2("all");
        setQ("");
    }, [brandId]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                if (isHeelsAndTea) {
                    const [prodRes, structRes] = await Promise.all([
                        api.get("/products"),
                        api.get("/catalog/structure", { params: { brand: brandId } }),
                    ]);
                    setProducts(prodRes.data);
                    setGroups(structRes.data.groups || []);
                    setCategories((structRes.data.categories || []).filter((c) => !c.archived));
                    setFeatured(structRes.data.featured || []);
                } else {
                    const [prodRes, catRes] = await Promise.all([
                        api.get("/products"),
                        api.get("/categories", { params: { brand: brandId } }),
                    ]);
                    setProducts(prodRes.data);
                    setCategories((catRes.data || []).filter((c) => !c.archived));
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [brandId, isHeelsAndTea]);

    const activeLevel1Group = groups.find((g) => g.slug === level1);
    const japanSelected = level1 === "japan";
    const level2Items = useMemo(() => {
        if (!isHeelsAndTea) return [];
        const base = categories.map((c) => ({ kind: "category", value: c.name, label: c.name }));
        if (japanSelected) {
            return [
                ...base,
                ...featured.filter((f) => f.region_slug === "japan" && !f.archived).map((f) => ({
                    kind: "brand", value: f.product_brand, label: f.display_name,
                })),
            ];
        }
        return base;
    }, [isHeelsAndTea, categories, featured, japanSelected]);

    const filtered = useMemo(() => {
        const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
        // Hide-out-of-stock honours the global "Hide Out-of-Stock Products"
        // setting for every brand. Effective stock = sum(variants[].stock) when
        // variants exist, else parent stock — handled by productStockTotal().
        const hideOOS = settings.hide_out_of_stock !== false;
        const list = products.filter((p) => {
            // Default rule: hide zero-stock products.
            if (p.status === "archived") return false;
            if (p.status === "draft") return false;
            if (hideOOS && productStockTotal(p) <= 0) return false;
            if (isHeelsAndTea) {
                if (level1 !== "all" && activeLevel1Group) {
                    if (activeLevel1Group.kind === "region" && p.region !== level1) return false;
                    if (activeLevel1Group.kind === "collection" && !(p.collections || []).includes(level1)) return false;
                }
                if (level2 !== "all") {
                    const item = level2Items.find((i) => i.value === level2);
                    if (item?.kind === "brand") {
                        if ((p.product_brand || "") !== item.value) return false;
                    } else if (p.category !== level2) return false;
                }
            } else if (category && p.category !== category) return false;
            if (advCategory && p.category !== advCategory) return false;
            if (carat && !(p.carat || "").toLowerCase().includes(carat.toLowerCase())) return false;
            if (size) {
                const sl = size.toLowerCase();
                const parentMatch = (p.size || "").toLowerCase().includes(sl);
                const vMatch = (p.variants || []).some((v) => (v.size || v.label || "").toLowerCase().includes(sl));
                if (!parentMatch && !vMatch) return false;
            }
            if (priceFilterActive && (p.retail_price < price[0] || p.retail_price > price[1])) return false;
            if (tokens.length) {
                const hay = [
                    p.name, p.sku, p.category, p.carat, p.size, p.name_jp, p.collection,
                    ...(p.variants || []).map((v) => `${v.sku} ${v.label} ${v.carat || ""}`),
                ].filter(Boolean).join(" ").toLowerCase();
                if (!tokens.every((t) => hay.includes(t))) return false;
            }
            return true;
        });
        const sorted = [...list];
        const total = productStockTotal;
        switch (sort) {
            case "name_asc": sorted.sort((a, b) => a.name.localeCompare(b.name)); break;
            case "name_desc": sorted.sort((a, b) => b.name.localeCompare(a.name)); break;
            case "price_asc": sorted.sort((a, b) => a.retail_price - b.retail_price); break;
            case "price_desc": sorted.sort((a, b) => b.retail_price - a.retail_price); break;
            case "stock_desc": sorted.sort((a, b) => total(b) - total(a)); break;
            case "stock_asc": sorted.sort((a, b) => total(a) - total(b)); break;
            default:
                sorted.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        }
        return sorted;
    }, [products, q, category, advCategory, carat, size, price, sort, settings.hide_out_of_stock, isHeelsAndTea, level1, level2, activeLevel1Group, level2Items]);

    const brandName = BRAND_META[brandId]?.name || "Catalog";
    const canEdit = canEditRole(me?.role);

    return (
        <div className="animate-fade-up w-full max-w-full">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                <div className="min-w-0">
                    <div className="eyebrow">Catalog</div>
                    <h1 className="font-serif text-4xl md:text-5xl mt-1 tracking-tight break-words">
                        {brandName} Collection
                    </h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="border border-mist rounded-full p-1 flex items-center" data-testid="view-toggle">
                        <button
                            onClick={() => setView("catalog")}
                            data-testid="view-catalog"
                            className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest flex items-center gap-1 ${
                                view === "catalog" ? "bg-sage-dark text-white" : "text-neutral-500"
                            }`}
                        >
                            <SquaresFour size={14} weight="light" /> Catalog
                        </button>
                        <button
                            onClick={() => setView("table")}
                            data-testid="view-table"
                            className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest flex items-center gap-1 ${
                                view === "table" ? "bg-sage-dark text-white" : "text-neutral-500"
                            }`}
                        >
                            <Rows size={14} weight="light" /> Table
                        </button>
                    </div>
                    {canEdit && (
                        <Link
                            to={isHeelsAndTea && level1 !== "all" && activeLevel1Group?.kind === "region" ? `/products/new?region=${level1}` : "/products/new"}
                            data-testid="add-product-link"
                            className="inline-flex items-center gap-1 bg-sage-dark text-white px-3 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep transition-colors"
                        >
                            <Plus size={14} weight="bold" /> New Product
                        </Link>
                    )}
                </div>
            </div>

            {/* Search */}
            <div className="relative mb-5">
                <MagnifyingGlass
                    size={22}
                    weight="light"
                    className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-400"
                />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search products, SKU, category, carat, size…"
                    data-testid="catalog-search-input"
                    className="w-full pl-8 pr-4 py-3 border-b border-mist focus:border-sage-dark outline-none font-serif text-lg md:text-2xl bg-transparent transition-colors"
                />
            </div>

            {/* Quick category chips — dynamic from active brand.
             *  Heels & Tea uses a 2-level nav (region/collection + category/featured brand). */}
            {isHeelsAndTea ? (
                <>
                    <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-3 -mx-1 px-1 scrollbar-none" data-testid="catalog-level1">
                        <button
                            onClick={() => { setLevel1("all"); setLevel2("all"); }}
                            data-testid="level1-chip-all"
                            className={`shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                                level1 === "all" ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                            }`}
                        >
                            All
                        </button>
                        {groups.filter((g) => !g.archived).map((g) => (
                            <button
                                key={g.id}
                                onClick={() => { setLevel1(g.slug); setLevel2("all"); }}
                                data-testid={`level1-chip-${g.slug}`}
                                className={`shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                                    level1 === g.slug ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                                }`}
                            >
                                <span className="mr-1">{g.icon}</span>{g.name}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1 scrollbar-none" data-testid="catalog-level2">
                        <button
                            onClick={() => setLevel2("all")}
                            data-testid="level2-chip-all"
                            className={`shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                                level2 === "all" ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                            }`}
                        >
                            All
                        </button>
                        {level2Items.map((it) => (
                            <button
                                key={`${it.kind}-${it.value}`}
                                onClick={() => setLevel2(level2 === it.value ? "all" : it.value)}
                                data-testid={`level2-chip-${it.value}`}
                                className={`shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                                    level2 === it.value ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                                }`}
                            >
                                {it.label}
                            </button>
                        ))}
                    </div>
                </>
            ) : (
                <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1 scrollbar-none" data-testid="catalog-chips">
                    <button
                        onClick={() => setCategory(null)}
                        data-testid="filter-chip-all"
                        className={`shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                            !category ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                        }`}
                    >
                        All
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setCategory(category === c.name ? null : c.name)}
                            data-testid={`filter-chip-cat-${c.name}`}
                            className={`shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                                category === c.name ? "bg-sage-dark text-white border-sage-dark" : "border-mist text-neutral-500 hover:border-sage-dark"
                            }`}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Sort & advanced */}
            <div className="flex items-center gap-2 flex-wrap mb-6">
                <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger
                        data-testid="sort-select"
                        className="h-8 rounded-full text-xs uppercase tracking-widest border-mist text-neutral-600 bg-transparent hover:border-sage-dark min-w-[160px]"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {SORT_OPTIONS.map((o) => (
                            <SelectItem key={o.key} value={o.key} data-testid={`sort-${o.key}`}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Sheet>
                    <SheetTrigger asChild>
                        <button
                            data-testid="advanced-filter-trigger"
                            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border border-mist text-neutral-500 hover:border-sage-dark"
                        >
                            <SlidersHorizontal size={14} weight="light" /> Advanced
                        </button>
                    </SheetTrigger>
                    <SheetContent className="bg-ivory">
                        <SheetHeader>
                            <SheetTitle className="font-serif text-2xl">Advanced Filter</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6 space-y-6">
                            <div>
                                <label className="eyebrow">Category</label>
                                <select
                                    value={advCategory}
                                    onChange={(e) => setAdvCategory(e.target.value)}
                                    className="mt-1 w-full border border-mist rounded-sm px-3 py-2 bg-white text-sm"
                                    data-testid="adv-category"
                                >
                                    <option value="">All</option>
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="eyebrow">Carat</label>
                                    <input
                                        value={carat}
                                        onChange={(e) => setCarat(e.target.value)}
                                        placeholder="e.g. 1"
                                        className="mt-1 w-full border border-mist rounded-sm px-3 py-2 bg-white text-sm"
                                        data-testid="adv-carat"
                                    />
                                </div>
                                <div>
                                    <label className="eyebrow">Size</label>
                                    <input
                                        value={size}
                                        onChange={(e) => setSize(e.target.value)}
                                        placeholder="e.g. 6 or 16 cm"
                                        className="mt-1 w-full border border-mist rounded-sm px-3 py-2 bg-white text-sm"
                                        data-testid="adv-size"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="eyebrow">
                                    Price Range ({formatIDR(price[0])} — {formatIDR(price[1])})
                                </label>
                                <Slider value={price} onValueChange={setPrice} min={PRICE_MIN} max={PRICE_MAX} step={100000} className="mt-4" />
                                {!priceFilterActive && (
                                    <p className="text-[11px] text-neutral-500 mt-2">Showing all prices — move a slider handle to apply a range.</p>
                                )}
                            </div>
                            <button
                                onClick={() => {
                                    setAdvCategory("");
                                    setCarat("");
                                    setSize("");
                                    setPrice([PRICE_MIN, PRICE_MAX]);
                                }}
                                className="w-full border border-mist rounded-sm py-2 text-xs uppercase tracking-widest hover:border-sage-dark"
                                data-testid="adv-reset"
                            >
                                Reset
                            </button>
                        </div>
                    </SheetContent>
                </Sheet>
                <span className="ml-auto text-xs text-neutral-500">{filtered.length} items</span>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-400 py-24 text-center">Loading catalog…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-mist rounded-sm bg-white p-10 md:p-16 text-center">
                    <div className="eyebrow">Empty</div>
                    <p className="font-serif text-2xl mt-2">No products matched.</p>
                    <p className="text-sm text-neutral-500 mt-1">Try adjusting the search or filters.</p>
                </div>
            ) : view === "catalog" ? (
                <div
                    className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6"
                    data-testid="catalog-grid"
                >
                    {filtered.map((p) => (
                        <ProductCard
                            key={p.id}
                            product={p}
                            regionBadge={isHeelsAndTea ? groups.find((g) => g.slug === p.region && g.kind === "region") : null}
                        />
                    ))}
                </div>
            ) : (
                <TableView items={filtered} />
            )}
        </div>
    );
}

function TableView({ items }) {
    const { settings } = useSettings();
    return (
        <div className="border border-mist rounded-sm bg-white overflow-x-auto -mx-4 md:mx-0" data-testid="product-table">
            <table className="w-full text-sm min-w-[720px]">
                <thead>
                    <tr className="text-left border-b border-mist">
                        {["", "Product", "SKU", "Category", "Variants", "Price", "Stock", "Status"].map((h, i) => (
                            <th key={i} className="eyebrow font-normal p-4 whitespace-nowrap">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((p) => {
                        const main = p.images?.find((i) => i.is_main) || p.images?.[0];
                        return (
                            <tr
                                key={p.id}
                                onClick={() => (window.location.href = `/products/${p.id}`)}
                                className="border-b border-mist hover:bg-[#F9F8F5] cursor-pointer"
                                data-testid={`table-row-${p.sku}`}
                            >
                                <td className="p-3 w-20">
                                    <div className="w-14 h-14 bg-[#F6F5F2] overflow-hidden rounded-sm flex items-center justify-center p-1">
                                        {main && (
                                            <img src={fileUrl(main.path)} alt="" className="w-full h-full object-contain" />
                                        )}
                                    </div>
                                </td>
                                <td className="p-4">
                                    <div className="font-serif text-base">{p.name}</div>
                                </td>
                                <td className="p-4 font-mono text-xs font-medium text-ink">{p.sku}</td>
                                <td className="p-4">{p.category}</td>
                                <td className="p-4 text-neutral-500">
                                    {p.variants?.length ? `${p.variants.length} variants` : "—"}
                                </td>
                                <td className="p-4 whitespace-nowrap">{formatIDR(p.retail_price)}</td>
                                <td className="p-4">{p.stock}</td>
                                <td className="p-4">
                                    <StockPill status={p.stock_status} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
